import apiClient from '../lib/api';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CreateOrderRequest {
  auctionId: number;
  buyerName: string;
  buyerSurname: string;
  buyerEmail: string;
  buyerPhone: string;
  deliveryStreet: string;
  deliveryAptNumber?: string;
  deliveryCity: string;
  deliveryZip: string;
  billingStreet?: string;
  billingAptNumber?: string;
  billingCity?: string;
  billingZip?: string;
  sameAddress: boolean;
  /** Share-link code if the buyer is redeeming a discount link. */
  shareLinkCode?: string;
}

export interface OrderResponse {
  orderId: number;
  status: 'Queued' | 'AwaitingPayment' | 'Processing' | 'Paid' | 'Cancelled' | 'Expired';
  price: number;
  currency: string;
  /** Stripe client_secret — present only when status === 'AwaitingPayment'. */
  clientSecret?: string;
  awaitingPaymentExpiresAtUtc?: string;
  queuePosition: number;
}

export interface QueuePositionResponse {
  auctionId: number;
  status: 'NotInQueue' | OrderResponse['status'];
  position: number;
  clientSecret?: string;
  awaitingPaymentExpiresAtUtc?: string;
  orderId?: number;
}

// ─── Endpoints ──────────────────────────────────────────────────────────────

/** POST /Order/api/v1/create [Authorize] */
export const createOrder = async (data: CreateOrderRequest): Promise<OrderResponse> => {
  const response = await apiClient.post<OrderResponse>('/Order/api/v1/create', data);
  return response.data;
};

/** GET /Order/api/v1/{orderId}/status [Authorize] */
export const getOrderStatus = async (orderId: number | string): Promise<OrderResponse> => {
  const response = await apiClient.get<OrderResponse>(`/Order/api/v1/${orderId}/status`);
  return response.data;
};

/** POST /Order/api/v1/{orderId}/cancel [Authorize] */
export const cancelOrder = async (orderId: number | string): Promise<void> => {
  await apiClient.post(`/Order/api/v1/${orderId}/cancel`);
};

/**
 * GET /api/v1/orders/me/queue-position?auctionId={n} [Authorize]
 *
 * Polled by the cart page every 2-3s while the user is in the queue. When the
 * status flips from Queued → AwaitingPayment the response carries the Stripe
 * clientSecret and the FE proceeds with confirmCardPayment.
 */
export const getMyQueuePosition = async (auctionId: number): Promise<QueuePositionResponse> => {
  const response = await apiClient.get<QueuePositionResponse>(
    `/api/v1/orders/me/queue-position`,
    { params: { auctionId } },
  );
  return response.data;
};
